import type { FC } from 'react';
import React, { useCallback, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Card,
  Stack,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell as TableCellOriginal,
  TableContainer as TableContainerOriginal,
  TableHead,
  TableRow,
  TableSortLabel,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import type { HeadCell } from '@pflegenavi/web-components';
import {
  TableFillerTextStyled,
  TableNoData,
  TableSkeleton,
  Label,
  NumberLabel,
  useFileUrlToTypeMapping,
} from '@pflegenavi/web-components';
import { useTranslation } from 'react-i18next';
import { useFormatting, useLocale } from '@pflegenavi/frontend/localization';
import type { ReceiptPhoenix } from '@pflegenavi/frontend/api-nursing-home';
import { useReceiptImages } from '@pflegenavi/frontend/api-nursing-home';
import { ReceiptState } from '@pflegenavi/shared/api';
import { YEAR_MONTH_DAY_SHORT_FORMAT } from '@pflegenavi/shared/constants';
import { TransactionTypeLogo } from '../general/TransactionTypeLogo';
import { PendingIconOverlay } from '../common/PendingIconOverlay';
import {
  IconTooltip,
  EnlargeImageGallery,
} from '@pflegenavi/transaction-components';
import { useUrlsToImages } from '@pflegenavi/shared-frontend/platform';

enum PendingReceiptsColumns {
  id = 'id',
  receiptType = 'receiptType',
  serviceProvider = 'serviceProvider',
  type = 'type',
  amount = 'amount',
  empty = 'empty',
  date = 'date',
  receipt = 'receipt',
}

interface PendingReceiptsSectionProps {
  receipts: ReceiptPhoenix[];
  isLoading: boolean;
  onReceiptClick?: (receipt: ReceiptPhoenix) => void;
  receiptState?: ReceiptState;
}

export const PendingReceiptsSection: FC<PendingReceiptsSectionProps> = ({
  receipts,
  isLoading,
  onReceiptClick,
  receiptState = ReceiptState.Pending,
}) => {
  const { t } = useTranslation();

  // Helper function to get appropriate translation keys based on receipt state
  const getTranslationKeys = () => {
    if (receiptState === ReceiptState.AwaitingSettlement) {
      return {
        title: 'receipts.awaiting-settlement-receipts',
        noData: 'receipts.no-awaiting-settlement-receipts',
      };
    }

    // Default to pending
    return {
      title: 'receipts.pending-receipts',
      noData: 'receipts.no-pending-receipts',
    };
  };

  const translationKeys = getTranslationKeys();

  const [receiptImageIds, setReceiptImageIds] = useState<string[] | undefined>(
    undefined
  );

  const { receiptImages: receiptImageUrls, ImageUrlsLoading } = useUrlsToImages(
    {
      receiptImageIds,
      useReceiptImages,
    }
  );

  const imageUrlToTypeMap = useFileUrlToTypeMapping(receiptImageUrls);

  const handleReceiptImageClick = useCallback((imageIds: string[]) => {
    setReceiptImageIds(imageIds);
  }, []);

  const handleCloseImageGallery = useCallback(() => {
    setReceiptImageIds(undefined);
  }, []);

  const TABLE_HEAD = useMemo<Array<HeadCell<PendingReceiptsColumns>>>(
    () => [
      {
        id: PendingReceiptsColumns.id,
        label: 'ID',
        align: 'left',
      },
      {
        id: PendingReceiptsColumns.receiptType,
        label: t('receipts.type'),
        align: 'left',
      },
      {
        id: PendingReceiptsColumns.serviceProvider,
        label: t('receipts.serviceProvider'),
        align: 'left',
      },
      {
        id: PendingReceiptsColumns.type,
        label: t('common.type'),
        align: 'left',
      },
      {
        id: PendingReceiptsColumns.amount,
        label: t('common.amount'),
        align: 'right',
        width: 2,
      },
      {
        id: PendingReceiptsColumns.empty,
        label: '',
        align: 'left',
      },
      {
        id: PendingReceiptsColumns.date,
        label: t('common.date'),
        align: 'left',
      },
      {
        id: PendingReceiptsColumns.receipt,
        label: t('resident.transactions.table.receipt'),
        align: 'center',
      },
    ],
    [t]
  );

  if (!isLoading && receipts.length === 0) {
    return null;
  }

  return (
    <Card sx={{ mb: 3 }}>
      <Tabs
        allowScrollButtonsMobile
        variant="scrollable"
        scrollButtons="auto"
        value="pending"
        sx={{
          px: 2,
          bgcolor: 'background.neutral',
        }}
      >
        <Tab
          disableRipple
          value="pending"
          label={
            <Stack
              spacing={1}
              direction="row"
              sx={{
                alignItems: 'center',
              }}
            >
              <div>{t(translationKeys.title)}</div>
              <NumberLabel color="info" variant="ghost">
                {receipts.length}
              </NumberLabel>
            </Stack>
          }
        />
      </Tabs>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {TABLE_HEAD.map((head) => (
                <TableCell
                  key={head.id}
                  align={head.align}
                  sx={{
                    width: head.width,
                    minWidth: head.minWidth,
                    maxWidth: head.maxWidth,
                  }}
                >
                  <TableSortLabel
                    disabled
                    sx={{
                      lineHeight: 'normal',
                    }}
                  >
                    {head.label.charAt(0).toUpperCase() + head.label.slice(1)}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading &&
              Array.from(Array(3).keys()).map((row: number) => (
                <TableSkeleton key={`row-${row}`} colSpan={TABLE_HEAD.length} />
              ))}

            {!isLoading && receipts?.length === 0 && (
              <TableNoData colSpan={TABLE_HEAD.length} isNotFound>
                <TableFillerTextStyled>
                  {t(translationKeys.noData)}
                </TableFillerTextStyled>
              </TableNoData>
            )}

            {!isLoading &&
              receipts.map((receipt) => (
                <PendingReceiptRow
                  key={receipt.id}
                  receipt={receipt}
                  onReceiptImageClick={handleReceiptImageClick}
                />
              ))}

            {!isLoading && (
              <EnlargeImageGallery
                loading={ImageUrlsLoading}
                open={Boolean(receiptImageIds)}
                handleClose={handleCloseImageGallery}
                initialSelectedIndex={0}
                imageUrls={receiptImageUrls || []}
                imageUrlToTypeMap={imageUrlToTypeMap}
              />
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

interface PendingReceiptRowProps {
  receipt: ReceiptPhoenix;
  onReceiptImageClick: (imageIds: string[]) => void;
}

const PendingReceiptRow: FC<PendingReceiptRowProps> = ({
  receipt,
  onReceiptImageClick,
}) => {
  const theme = useTheme();
  const { fCurrencyCents } = useFormatting();
  const locale = useLocale();
  const { t } = useTranslation();

  const receiptType =
    receipt.receipt_type?.name || t('receipts.general-receipt');
  const serviceProvider = receipt.service_provider?.name || '--';

  const amountTooltipTitle = useMemo(() => {
    let title;

    if (
      receipt.additional_fee_amount !== 0 &&
      receipt.included_fee_amount !== 0
    ) {
      title = t('tooltip.included-and-additional-fee', {
        additionalFeeAmount: fCurrencyCents(receipt.additional_fee_amount),
        includedFeeAmount: fCurrencyCents(receipt.included_fee_amount),
      });
    } else if (receipt.additional_fee_amount !== 0) {
      title = t('tooltip.additional-fee', {
        additionalFeeAmount: fCurrencyCents(receipt.additional_fee_amount),
      });
    } else if (receipt.included_fee_amount !== 0) {
      title = t('tooltip.included-fee', {
        includedFeeAmount: fCurrencyCents(receipt.included_fee_amount),
      });
    }

    return title;
  }, [fCurrencyCents, t, receipt]);

  const renderReceiptIcon = useMemo(() => {
    if (
      receipt.receipt_image_ids &&
      receipt.receipt_image_ids.length > 0 &&
      !receipt.receipt_not_available
    ) {
      return (
        <IconTooltip
          title={t('tooltip.show-receipt-image')}
          color={theme.palette.success.main}
          icon="check"
          onClick={() => {
            if (
              receipt.receipt_image_ids &&
              receipt.receipt_image_ids.length > 0
            ) {
              onReceiptImageClick(receipt.receipt_image_ids);
            }
          }}
        />
      );
    }

    return (
      <IconTooltip
        title={t('tooltip.no-image-available')}
        color={theme.palette.grey['400']}
        icon="remove"
      />
    );
  }, [
    receipt.receipt_image_ids,
    receipt.receipt_not_available,
    onReceiptImageClick,
    t,
    theme.palette.grey,
    theme.palette.success.main,
  ]);

  return (
    <TableRow hover>
      {/* ID Column */}
      <TableCell
        align="left"
        sx={{
          maxWidth: 182,
          color: 'text.secondary',
          fontSize: 13,
        }}
      >
        --
      </TableCell>
      {/* Receipt Type Column */}
      <TableCell align="left">
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
          }}
        >
          <Box sx={{ position: 'relative' }}>
            <TransactionTypeLogo size={40} logoType={receiptType} />
            <PendingIconOverlay />
          </Box>
          <Typography
            variant="subtitle2"
            sx={{ ml: 2, fontWeight: 600 }}
            noWrap
          >
            {receiptType}
          </Typography>
        </Stack>
      </TableCell>
      {/* Service Provider Column */}
      <TableCell
        align="left"
        sx={{
          maxWidth: 270,
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {serviceProvider}
      </TableCell>
      {/* Type Column */}
      <TableCell align="left">
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>
          {receipt.receipt_state === ReceiptState.AwaitingSettlement
            ? t('receipts.awaiting-settlement-type')
            : t('receipts.pending-type')}
        </Typography>
      </TableCell>
      {/* Amount Column */}
      <TableCell align="right">
        {receipt.additional_fee_amount !== 0 ||
        receipt.included_fee_amount !== 0 ? (
          <Tooltip arrow placement="right" title={amountTooltipTitle}>
            <span>
              <Label variant="ghost" color="info">
                {fCurrencyCents(
                  -receipt.amount - receipt.additional_fee_amount
                )}
              </Label>
            </span>
          </Tooltip>
        ) : (
          <Label variant="ghost" color="info">
            {fCurrencyCents(-receipt.amount - receipt.additional_fee_amount)}
          </Label>
        )}
      </TableCell>
      {/* Empty Column */}
      <TableCell align="left"> </TableCell>
      {/* Date Column */}
      <TableCell align="left">
        {new Date(receipt.receipt_date).toLocaleDateString(
          locale,
          YEAR_MONTH_DAY_SHORT_FORMAT
        )}
      </TableCell>
      {/* Receipt Column */}
      <TableCell
        align="center"
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        {renderReceiptIcon}
      </TableCell>
    </TableRow>
  );
};

const TableCell = styled(TableCellOriginal)`
  padding: 12px 16px;
`;

const TableContainer = styled(TableContainerOriginal)(({ theme }) =>
  theme.unstable_sx({
    width: '100%',
  })
);
